import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, debounceTime, distinctUntilChanged, switchMap } from 'rxjs';
import { LayoutService } from '../../layouts/layout.service';
import { DataTableComponent, ColumnDef } from '../../shared/components/data-table/data-table';
import { SelectComponent, SelectOption } from '../../shared/components/select/select';
import { DatePickerComponent } from '../../shared/components/date-picker/date-picker';
import { Person, PersonDetail, PersonType, personTagline } from '../../shared/models/person.models';
import { PersonService } from '../../core/services/person.service';
import { ToastService } from '../../core/services/toast.service';
import { apiErrorHasCode, apiErrorMessage } from '../../shared/utils/api-error';

// Person.Sex is nvarchar(1) in the API, so a one-letter code is stored.
const SEX_OPTIONS: SelectOption[] = [
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
  { value: 'X', label: 'Non-binary' },
  { value: 'U', label: 'Prefer not to say' },
];

@Component({
  selector: 'app-persons',
  imports: [DataTableComponent, SelectComponent, DatePickerComponent],
  templateUrl: './persons.html',
})
export class PersonsComponent implements OnInit {
  private layout = inject(LayoutService);
  private personService = inject(PersonService);
  private toast = inject(ToastService);
  private destroyRef = inject(DestroyRef);

  private nameTpl = viewChild.required<TemplateRef<{ $implicit: Person }>>('nameTpl');
  private typeTpl = viewChild.required<TemplateRef<{ $implicit: Person }>>('typeTpl');

  // List state
  protected loading = signal(false);
  protected persons = signal<Person[]>([]);
  protected total = signal(0);
  protected page = signal(1);
  protected readonly pageSize = 10;
  private personTypes = signal<PersonType[]>([]);
  // The list filter goes by type code (the API filters by personTypeCode)…
  protected typeOptions = computed((): SelectOption[] => [
    { value: '', label: 'All' },
    ...this.personTypes()
      .filter((t) => !!t.personTypeCode)
      .map((t) => ({ value: t.personTypeCode!, label: t.name })),
  ]);
  // …but create/update send personTypeId, so the form selects must use ids.
  protected detailTypeOptions = computed((): SelectOption[] =>
    this.personTypes().map((t) => ({ value: t.id, label: t.name })),
  );
  protected search = signal('');
  protected selectedTypeCode = signal('');
  protected readonly sexOptions = SEX_OPTIONS;

  // Create modal
  protected showCreateModal = signal(false);
  protected creating = signal(false);
  protected cPersonTypeId = signal('');
  protected cFirstName = signal('');
  protected cSurnames = signal('');
  protected cPhone = signal('');
  protected cDob = signal('');
  protected cSex = signal('');
  protected cMedicaidCIN = signal('');
  /** The API rejected the CIN as already used by another person (JCCA-173). */
  protected cCinInUse = signal(false);
  protected cHrFileNumber = signal('');

  // Detail/Edit modal
  protected showDetailModal = signal(false);
  protected detailLoading = signal(false);
  protected detailSaving = signal(false);
  protected detail = signal<PersonDetail | null>(null);
  /** Persons synced from other systems are read-only; the API decides by record origin (JCCA-177). */
  protected detailReadOnly = computed(() => this.detail()?.isEditable === false);
  protected readonly tagline = personTagline;
  protected dPersonTypeId = signal('');
  protected dFirstName = signal('');
  protected dSurnames = signal('');
  protected dPhone = signal('');
  protected dDob = signal('');
  protected dSex = signal('');
  protected dMedicaidCIN = signal('');
  protected dCinInUse = signal(false);
  protected dHrFileNumber = signal('');

  protected columns = computed((): ColumnDef<Person>[] => [
    { key: 'name', header: 'Person Name', cellTemplate: this.nameTpl() },
    { key: 'type', header: 'Person Type', cellTemplate: this.typeTpl() },
    {
      key: 'dob',
      header: 'DOB',
      cell: (p) => this.formatDate(p.dob),
      cellClass: 'text-slate-600 whitespace-nowrap',
    },
    {
      key: 'cin',
      header: 'CIN',
      cell: (p) => p.cin ?? '--',
      cellClass: 'text-slate-600 font-mono',
    },
    {
      key: 'phone',
      header: 'Phone Number',
      cell: (p) => p.phoneNumber ?? '--',
      cellClass: 'text-slate-600 whitespace-nowrap',
    },
  ]);

  private search$ = new Subject<string>();
  private load$ = new Subject<void>();

  ngOnInit(): void {
    this.layout.set({
      title: 'People Directory',
      breadcrumb: 'Search and manage global records for clients, staff, and external personnel.',
      showBackButton: false,
      headerAction: {
        label: 'Create Person',
        icon: 'plus',
        onClick: () => this.showCreateModal.set(true),
      },
    });

    this.loadTypes();

    this.load$
      .pipe(
        switchMap(() => {
          this.loading.set(true);
          return this.personService.getPersons({
            // `search` (not `filter`) also matches Medicaid CIN, as the placeholder promises.
            search: this.search().trim() || undefined,
            personTypeCode: this.selectedTypeCode() || undefined,
            pageSize: this.pageSize,
            pageIndex: this.page() - 1,
          });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res) => {
          this.persons.set(res.persons ?? []);
          this.total.set(res.total);
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });

    this.search$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.page.set(1);
        this.load$.next();
      });

    this.load$.next();
  }

  private loadTypes(): void {
    this.personService
      .getPersonTypes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (types) => this.personTypes.set(types),
      });
  }

  protected onSearch(value: string): void {
    this.search.set(value);
    this.search$.next(value);
  }

  protected onTypeChange(value: string): void {
    this.selectedTypeCode.set(value);
    this.page.set(1);
    this.load$.next();
  }

  protected onPageChange(page: number): void {
    this.page.set(page);
    this.load$.next();
  }

  protected onRowAction(person: Person): void {
    this.showDetailModal.set(true);
    this.detailLoading.set(true);
    this.personService
      .getPersonById(person.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (p) => {
          this.detail.set(p);
          this.dPersonTypeId.set(p.personTypeId ?? '');
          this.dFirstName.set(p.firstName ?? '');
          this.dSurnames.set(p.surnames ?? '');
          this.dPhone.set(p.phoneNumber ?? '');
          this.dDob.set(p.dob ?? '');
          this.dSex.set(p.sex ?? '');
          this.dMedicaidCIN.set(p.medicaidCIN ?? '');
          this.dCinInUse.set(false);
          this.dHrFileNumber.set(p.hrFileNumber ?? '');
          this.detailLoading.set(false);
        },
        error: () => this.detailLoading.set(false),
      });
  }

  protected onSaveDetail(): void {
    const p = this.detail();
    if (!p) return;
    this.detailSaving.set(true);
    this.personService
      .updatePerson({
        id: p.id,
        personTypeId: this.dPersonTypeId() || null,
        personRecordOriginId: this.detail()?.personRecordOriginId ?? null,
        firstName: this.dFirstName(),
        surnames: this.dSurnames(),
        phoneNumber: this.dPhone() || null,
        dob: this.toDateOnly(this.dDob()),
        sex: this.dSex() || null,
        medicaidCIN: this.dMedicaidCIN() || null,
        hrFileNumber: this.dHrFileNumber() || null,
        hrisId: p.hrisId ?? null,
        evolvPeopleId: p.evolvPeopleId ?? null,
        estherClientKey: p.estherClientKey ?? null,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.detailSaving.set(false);
          this.showDetailModal.set(false);
          this.toast.show('Person updated successfully');
          this.load$.next();
        },
        error: (err) => {
          this.detailSaving.set(false);
          this.dCinInUse.set(apiErrorHasCode(err, 'CinExist'));
          this.toast.show(apiErrorMessage(err, 'Failed to update person'), 'error');
        },
      });
  }

  protected onCreate(): void {
    const firstName = this.cFirstName().trim();
    const surnames = this.cSurnames().trim();
    if (!firstName || !surnames) return;
    this.creating.set(true);
    this.personService
      .createPerson({
        firstName,
        surnames,
        dob: this.toDateOnly(this.cDob()),
        sex: this.cSex() || null,
        phoneNumber: this.cPhone() || null,
        medicaidCIN: this.cMedicaidCIN() || null,
        hrFileNumber: this.cHrFileNumber() || null,
        personTypeId: this.cPersonTypeId() || null,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.creating.set(false);
          this.showCreateModal.set(false);
          this.toast.show('Person created successfully');
          this.resetCreateForm();
          this.page.set(1);
          this.load$.next();
        },
        error: (err) => {
          this.creating.set(false);
          this.cCinInUse.set(apiErrorHasCode(err, 'CinExist'));
          this.toast.show(apiErrorMessage(err, 'Failed to create person'), 'error');
        },
      });
  }

  private resetCreateForm(): void {
    this.cCinInUse.set(false);
    this.cPersonTypeId.set('');
    this.cFirstName.set('');
    this.cSurnames.set('');
    this.cPhone.set('');
    this.cDob.set('');
    this.cSex.set('');
    this.cMedicaidCIN.set('');
    this.cHrFileNumber.set('');
  }

  /** DOB is a DateOnly in the API: it only accepts YYYY-MM-DD (the picker may add a time). */
  private toDateOnly(value: string): string | null {
    return value ? value.slice(0, 10) : null;
  }

  protected initials(person: Person): string {
    return ((person.firstName?.[0] ?? '') + (person.surnames?.[0] ?? '')).toUpperCase();
  }

  protected formatDate(dob: string | null): string {
    if (!dob) return '--';
    const d = new Date(dob);
    if (isNaN(d.getTime())) return '--';
    return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  }
}
